import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Mic,
  Plus,
  Copy,
  ExternalLink,
  Trash2,
  Loader2,
  Sparkles,
  X,
  Users,
  TrendingUp,
  Mail,
  Send,
  CheckCircle2,
} from "lucide-react";

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("af_token")}`,
});

const STATUS_COLORS = {
  prospect: "bg-slate-100 text-slate-700",
  pitched: "bg-amber-100 text-amber-800",
  active: "bg-emerald-100 text-emerald-800",
  paused: "bg-rose-100 text-rose-800",
};

const TIER_COLORS = {
  1: "bg-violet-100 text-violet-800 border-violet-200",
  2: "bg-sky-100 text-sky-800 border-sky-200",
  3: "bg-slate-100 text-slate-700 border-slate-200",
};

const TIER_LABEL = { 1: "T1 · Must pitch", 2: "T2 · Strong fit", 3: "T3 · Bonus" };

export default function AdminPodcasterPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [kitOpen, setKitOpen] = useState(null); // partner with kit data
  const [kitLoading, setKitLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null); // single-send loader
  const [bulkSending, setBulkSending] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [form, setForm] = useState({
    podcast_name: "",
    host_name: "",
    audience_size: "",
    commission_pct: 40,
    tier: 2,
    contact_email: "",
    website: "",
    notes: "",
    status: "prospect",
  });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/partner/admin/partners`, {
        headers: authHeader(),
      });
      setPartners(r.data?.partners || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load partners");
    } finally {
      setLoading(false);
    }
  };

  const seedTop10 = async () => {
    if (
      !confirm(
        "Seed/refresh the full curated list of wholesale-real-estate podcasters across Tiers 1–3 (18 entries: Brent Daniels, Pace Morby, Jerry Norton, BiggerPockets, etc.)? Existing entries are backfilled with tier + commission updates.",
      )
    )
      return;
    setSeeding(true);
    try {
      const r = await axios.post(
        `${API}/partner/admin/seed-wholesale-podcasters`,
        {},
        { headers: authHeader() },
      );
      toast.success(
        `+${r.data?.total_created || 0} added · ${r.data?.total_updated || 0} refreshed`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  const createPartner = async (e) => {
    e?.preventDefault();
    if (!form.podcast_name || !form.host_name) {
      toast.error("Podcast name and host name are required");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...form,
        audience_size: form.audience_size ? parseInt(form.audience_size, 10) : 0,
        commission_pct: parseInt(form.commission_pct, 10) || 40,
        tier: parseInt(form.tier, 10) || 2,
      };
      await axios.post(`${API}/partner/admin/partners`, payload, {
        headers: authHeader(),
      });
      toast.success(`Added ${form.host_name}`);
      setForm({
        podcast_name: "",
        host_name: "",
        audience_size: "",
        commission_pct: 40,
        tier: 2,
        contact_email: "",
        website: "",
        notes: "",
        status: "prospect",
      });
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add partner");
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async (partner, status) => {
    try {
      await axios.patch(
        `${API}/partner/admin/partners/${partner.id}`,
        { status },
        { headers: authHeader() },
      );
      toast.success(`${partner.host_name} → ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status update failed");
    }
  };

  const removePartner = async (partner) => {
    if (!confirm(`Remove ${partner.host_name}? You can re-add later.`)) return;
    try {
      await axios.delete(`${API}/partner/admin/partners/${partner.id}`, {
        headers: authHeader(),
      });
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  const openKit = async (partner) => {
    setKitLoading(true);
    setKitOpen({ partner });
    try {
      const r = await axios.get(
        `${API}/partner/admin/partners/${partner.id}/pitch-kit`,
        { headers: authHeader() },
      );
      setKitOpen({ partner, kit: r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to build kit");
      setKitOpen(null);
    } finally {
      setKitLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const sendPitch = async (partner) => {
    if (!partner.contact_email) {
      toast.error(`No contact email saved for ${partner.host_name}`);
      return;
    }
    if (
      !confirm(
        `Send pitch to ${partner.host_name} at ${partner.contact_email}? Email goes out via Resend from hello@ispeedtoclose.com.`,
      )
    )
      return;
    setSendingId(partner.id);
    try {
      await axios.post(
        `${API}/partner/admin/partners/${partner.id}/send-pitch`,
        {},
        { headers: authHeader() },
      );
      toast.success(`Pitch sent to ${partner.host_name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSendingId(null);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = (visibleIds) => {
    setSelectedIds((prev) => {
      const allSelected = visibleIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(visibleIds);
    });
  };

  const bulkSend = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (
      !confirm(
        `Send pitch emails to ${ids.length} podcaster${ids.length === 1 ? "" : "s"}? Each email goes out via Resend from hello@ispeedtoclose.com.`,
      )
    )
      return;
    setBulkSending(true);
    try {
      const r = await axios.post(
        `${API}/partner/admin/send-pitch-bulk`,
        { partner_ids: ids },
        { headers: authHeader() },
      );
      const sent = r.data?.sent_count || 0;
      const failed = r.data?.failed_count || 0;
      if (sent > 0) toast.success(`Sent ${sent} pitch${sent === 1 ? "" : "es"}`);
      if (failed > 0) toast.error(`${failed} failed (check console)`);
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk send failed");
    } finally {
      setBulkSending(false);
    }
  };

  const totalVisits = partners.reduce((a, p) => a + (p.visits_total || 0), 0);
  const totalSignups = partners.reduce((a, p) => a + (p.signups_total || 0), 0);
  const totalReach = partners.reduce((a, p) => a + (p.audience_size || 0), 0);

  const [tierFilter, setTierFilter] = useState("all");
  const sorted = [...partners].sort((a, b) => {
    const ta = a.tier || 99;
    const tb = b.tier || 99;
    if (ta !== tb) return ta - tb;
    return (b.audience_size || 0) - (a.audience_size || 0);
  });
  const visible = tierFilter === "all" ? sorted : sorted.filter((p) => String(p.tier) === tierFilter);

  const tierCounts = partners.reduce((acc, p) => {
    const t = p.tier || 2;
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="admin-podcaster-partners">
      <div className="card p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm text-[var(--ink-2)]">
              <Mic size={14} /> Outreach
            </div>
            <h2 className="font-display font-bold text-2xl mt-1">
              Podcaster Partner Kit
            </h2>
            <p className="text-sm text-[var(--ink-2)] mt-1 max-w-2xl">
              Track every podcaster, YouTuber, and influencer you pitch.
              Generate a custom affiliate link, pitch email, and earnings
              projection for each one — then watch the signups roll in.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={seedTop10}
              disabled={seeding}
              data-testid="seed-top-10-btn"
              className="btn-secondary text-sm flex items-center gap-2"
            >
              {seeding ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Seed All Curated Podcasters (T1–T3)
            </button>
            <button
              onClick={() => setShowForm((v) => !v)}
              data-testid="add-partner-btn"
              className="btn-primary text-sm flex items-center gap-2"
            >
              <Plus size={14} /> Add Partner
            </button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <Stat label="Partners" value={partners.length} icon={Users} />
          <Stat label="Combined Reach" value={totalReach.toLocaleString()} icon={TrendingUp} />
          <Stat label="Total Visits" value={totalVisits.toLocaleString()} icon={ExternalLink} />
          <Stat label="Total Signups" value={totalSignups.toLocaleString()} icon={Mail} />
        </div>
      </div>

      {showForm && (
        <div className="card p-5" data-testid="add-partner-form">
          <form onSubmit={createPartner} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Podcast / Channel name *"
              value={form.podcast_name}
              onChange={(v) => setForm({ ...form, podcast_name: v })}
              testid="form-podcast-name"
            />
            <Input
              label="Host name *"
              value={form.host_name}
              onChange={(v) => setForm({ ...form, host_name: v })}
              testid="form-host-name"
            />
            <Input
              label="Audience size (subscribers / listeners)"
              type="number"
              value={form.audience_size}
              onChange={(v) => setForm({ ...form, audience_size: v })}
              testid="form-audience-size"
            />
            <Input
              label="Commission % (10–70)"
              type="number"
              value={form.commission_pct}
              onChange={(v) => setForm({ ...form, commission_pct: v })}
              testid="form-commission-pct"
            />
            <div>
              <label className="text-xs text-[var(--ink-2)]">Tier</label>
              <select
                value={form.tier}
                onChange={(e) => setForm({ ...form, tier: e.target.value })}
                data-testid="form-tier"
                className="w-full mt-1 px-3 py-2 rounded-lg border text-sm"
              >
                <option value={1}>T1 · Must pitch (industry leader)</option>
                <option value={2}>T2 · Strong fit (mid-size)</option>
                <option value={3}>T3 · Bonus / broader audience</option>
              </select>
            </div>
            <Input
              label="Contact email"
              type="email"
              value={form.contact_email}
              onChange={(v) => setForm({ ...form, contact_email: v })}
              testid="form-contact-email"
            />
            <Input
              label="Website / Podcast URL"
              value={form.website}
              onChange={(v) => setForm({ ...form, website: v })}
              testid="form-website"
            />
            <div className="md:col-span-2">
              <label className="text-xs text-[var(--ink-2)]">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                data-testid="form-notes"
                className="w-full mt-1 px-3 py-2 rounded-lg border text-sm"
              />
            </div>
            <div className="md:col-span-2 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn-secondary text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                data-testid="form-submit-btn"
                className="btn-primary text-sm flex items-center gap-2"
              >
                {creating && <Loader2 size={14} className="animate-spin" />}
                Add Partner
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        {/* Bulk action bar */}
        {!loading && selectedIds.size > 0 && (
          <div
            className="flex items-center justify-between gap-3 p-3 border-b bg-emerald-50"
            data-testid="bulk-action-bar"
          >
            <div className="text-sm font-semibold text-emerald-800">
              {selectedIds.size} podcaster{selectedIds.size === 1 ? "" : "s"} selected
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedIds(new Set())}
                className="btn-secondary text-xs"
              >
                Clear
              </button>
              <button
                onClick={bulkSend}
                disabled={bulkSending}
                data-testid="bulk-send-btn"
                className="btn-primary text-xs flex items-center gap-1.5"
              >
                {bulkSending ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Send size={12} />
                )}
                Send pitch to {selectedIds.size}
              </button>
            </div>
          </div>
        )}
        {/* Tier filter strip */}
        {!loading && partners.length > 0 && (
          <div className="flex items-center gap-1 p-2 border-b bg-slate-50/50" data-testid="tier-filter-strip">
            {[
              { v: "all", label: `All (${partners.length})` },
              { v: "1", label: `T1 (${tierCounts[1] || 0})` },
              { v: "2", label: `T2 (${tierCounts[2] || 0})` },
              { v: "3", label: `T3 (${tierCounts[3] || 0})` },
            ].map((t) => (
              <button
                key={t.v}
                onClick={() => setTierFilter(t.v)}
                data-testid={`tier-filter-${t.v}`}
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
                  tierFilter === t.v
                    ? "bg-[var(--primary)] text-white font-semibold"
                    : "text-[var(--ink-2)] hover:bg-slate-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
        {loading ? (
          <div className="p-10 text-center text-[var(--ink-2)] flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading partners…
          </div>
        ) : partners.length === 0 ? (
          <div className="p-10 text-center text-[var(--ink-2)]">
            No partners yet — click <strong>Seed All Curated Podcasters</strong> to
            import 18 hand-picked wholesale-real-estate podcasters across Tiers
            1–3 in one click.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="partners-table">
            <thead className="bg-slate-50 text-xs uppercase text-[var(--ink-2)]">
              <tr>
                <th className="p-3 w-8">
                  <input
                    type="checkbox"
                    data-testid="select-all-checkbox"
                    checked={
                      visible.length > 0 &&
                      visible.every((p) => selectedIds.has(p.id))
                    }
                    onChange={() => selectAllVisible(visible.map((p) => p.id))}
                  />
                </th>
                <th className="text-left p-3">Tier</th>
                <th className="text-left p-3">Podcast / Host</th>
                <th className="text-left p-3">Audience</th>
                <th className="text-left p-3">Rate</th>
                <th className="text-left p-3">Sent</th>
                <th className="text-left p-3">Visits</th>
                <th className="text-left p-3">Signups</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`partner-row-${p.slug}`}>
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      data-testid={`row-checkbox-${p.slug}`}
                    />
                  </td>
                  <td className="p-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase ${TIER_COLORS[p.tier] || TIER_COLORS[2]}`}
                      title={TIER_LABEL[p.tier] || ""}
                    >
                      T{p.tier || 2}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="font-semibold">{p.host_name}</div>
                    <div className="text-xs text-[var(--ink-2)]">
                      {p.podcast_name}
                    </div>
                  </td>
                  <td className="p-3">{(p.audience_size || 0).toLocaleString()}</td>
                  <td className="p-3">
                    <span className="badge bg-emerald-50 text-emerald-700">
                      {p.commission_pct}%
                    </span>
                  </td>
                  <td className="p-3">
                    {p.pitched_at ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs text-emerald-700"
                        title={p.pitched_at}
                        data-testid={`sent-stamp-${p.slug}`}
                      >
                        <CheckCircle2 size={12} />
                        {new Date(p.pitched_at).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="p-3">{p.visits_total}</td>
                  <td className="p-3 font-semibold">{p.signups_total}</td>
                  <td className="p-3">
                    <select
                      value={p.status}
                      onChange={(e) => updateStatus(p, e.target.value)}
                      data-testid={`status-select-${p.slug}`}
                      className={`text-xs px-2 py-1 rounded-md border-0 ${STATUS_COLORS[p.status] || "bg-slate-100"}`}
                    >
                      <option value="prospect">Prospect</option>
                      <option value="pitched">Pitched</option>
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                    </select>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => sendPitch(p)}
                        disabled={sendingId === p.id || !p.contact_email}
                        data-testid={`send-pitch-${p.slug}`}
                        title={
                          p.contact_email
                            ? `Send pitch to ${p.contact_email}`
                            : "No contact email saved"
                        }
                        className="p-2 rounded hover:bg-emerald-50 text-emerald-700 disabled:opacity-30 disabled:hover:bg-transparent"
                      >
                        {sendingId === p.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Send size={14} />
                        )}
                      </button>
                      <button
                        onClick={() => openKit(p)}
                        data-testid={`open-kit-${p.slug}`}
                        title="Generate pitch kit"
                        className="p-2 rounded hover:bg-slate-100"
                      >
                        <Sparkles size={14} />
                      </button>
                      <button
                        onClick={() => removePartner(p)}
                        data-testid={`delete-${p.slug}`}
                        title="Remove"
                        className="p-2 rounded hover:bg-rose-50 text-rose-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {kitOpen && (
        <KitModal
          state={kitOpen}
          loading={kitLoading}
          onClose={() => setKitOpen(null)}
          onCopy={copyToClipboard}
          onSend={sendPitch}
          sending={sendingId === kitOpen?.partner?.id}
        />
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl bg-slate-50 p-3">
      <div className="flex items-center gap-1.5 text-xs text-[var(--ink-2)]">
        <Icon size={12} /> {label}
      </div>
      <div className="font-display font-bold text-xl mt-1">{value}</div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs text-[var(--ink-2)]">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full mt-1 px-3 py-2 rounded-lg border text-sm"
      />
    </div>
  );
}

function KitModal({ state, loading, onClose, onCopy, onSend, sending }) {
  const { partner, kit } = state;
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      data-testid="kit-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <div className="text-xs text-[var(--ink-2)]">Pitch Kit</div>
            <h3 className="font-display font-bold text-xl">
              {partner.host_name} · {partner.podcast_name}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-slate-100" data-testid="kit-close">
            <X size={18} />
          </button>
        </div>

        {loading || !kit ? (
          <div className="p-10 text-center text-[var(--ink-2)] flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Building kit…
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Earnings projection */}
            <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 p-4">
              <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">
                Earnings Projection (one mention)
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <ProjStat label="Audience" value={kit.projection.audience.toLocaleString()} />
                <ProjStat label="Est. signups" value={kit.projection.signups_est.toLocaleString()} />
                <ProjStat label="Est. paid" value={kit.projection.paid_est.toLocaleString()} />
                <ProjStat label="Their cut / mo" value={kit.projection.mrr_partner_label} highlight />
              </div>
              <div className="text-xs text-emerald-800/80 mt-3">
                Assumes 1% click-through, 15% signup rate, 30% trial→paid on{" "}
                {kit.projection.plan_price_label} plan at {kit.projection.commission_pct}%
                recurring for 12 months ={" "}
                <strong>{kit.projection.annual_partner_label}</strong> per
                mention.
              </div>
            </div>

            {/* Share link */}
            <div>
              <div className="text-xs uppercase tracking-wide text-[var(--ink-2)] font-semibold mb-2">
                Their unique affiliate link
              </div>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={kit.share_url}
                  data-testid="kit-share-url"
                  className="flex-1 px-3 py-2 rounded-lg border text-sm bg-slate-50 font-mono"
                />
                <button
                  onClick={() => onCopy(kit.share_url, "Affiliate link")}
                  data-testid="kit-copy-url"
                  className="btn-secondary text-sm flex items-center gap-1"
                >
                  <Copy size={14} /> Copy
                </button>
              </div>
            </div>

            {/* Pitch email */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wide text-[var(--ink-2)] font-semibold">
                  Ready-to-send pitch email
                </div>
                <button
                  onClick={() => onCopy(kit.email, "Pitch email")}
                  data-testid="kit-copy-email"
                  className="btn-secondary text-xs flex items-center gap-1"
                >
                  <Copy size={12} /> Copy
                </button>
              </div>
              <pre
                data-testid="kit-email-body"
                className="text-xs bg-slate-50 p-3 rounded-lg whitespace-pre-wrap font-sans border max-h-72 overflow-y-auto"
              >
                {kit.email}
              </pre>
            </div>

            {partner.contact_email && (
              <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50 border">
                <div className="text-xs text-[var(--ink-2)]">
                  Send via Resend to{" "}
                  <strong className="text-[var(--ink)]">{partner.contact_email}</strong>
                  <div className="text-[10px] mt-0.5">
                    From: hello@ispeedtoclose.com · Reply-To: hello@ispeedtoclose.com
                  </div>
                </div>
                <button
                  onClick={() => onSend(partner)}
                  disabled={sending || partner.pitched_at}
                  data-testid="kit-send-btn"
                  className="btn-primary text-sm flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50"
                  title={partner.pitched_at ? "Already pitched" : "Send via Resend now"}
                >
                  {sending ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Send size={14} />
                  )}
                  {partner.pitched_at ? "Already sent ✓" : "Send pitch now"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ProjStat({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-emerald-700/70">{label}</div>
      <div
        className={`font-display font-bold ${highlight ? "text-2xl text-emerald-700" : "text-lg"}`}
      >
        {value}
      </div>
    </div>
  );
}
